/**
 * Shared "planned work" query and formatting for orient/start.
 *
 * Surfaces uncommitted drafts, in-progress features/decisions, and
 * open tensions as a single "Continue from" section with a concrete CTA.
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { kernelQuery } from "../client.js";

/** Loosely-typed session record returned by agent.recentSessions. */
export interface PriorSession {
  startedAt?: string | number;
  entriesCreated?: string[] | number;
  entriesModified?: string[] | number;
  [key: string]: unknown;
}

export interface PlannedWork {
  uncommittedDrafts: Array<{ name: string; collection: string }>;
  inProgressEntries: Array<{ name: string; collection: string; entryId: string }>;
  openTensions: Array<{ name: string; entryId: string }>;
}

export function buildPlannedWork(allEntries: any[]): PlannedWork {
  const result: PlannedWork = {
    uncommittedDrafts: [],
    inProgressEntries: [],
    openTensions: [],
  };

  for (const entry of allEntries) {
    if (entry.stratum === 'system') continue;

    const collection = entry.collectionSlug ?? entry.collection ?? "unknown";

    if (entry.status === "draft") {
      result.uncommittedDrafts.push({ name: entry.name, collection });
    }

    // BET-281: Use canonicalKey to identify tensions instead of hardcoded slug.
    // canonicalKey is optional on entries, so fall back to slug for backward compat.
    const isTension = entry.canonicalKey === "tension" || collection === "tensions";
    if (isTension && entry.workflowStatus === "open") {
      result.openTensions.push({ name: entry.name, entryId: entry.entryId ?? entry._id });
    }

    if (entry.workflowStatus === "in-progress") {
      result.inProgressEntries.push({
        name: entry.name,
        collection,
        entryId: entry.entryId ?? entry._id,
      });
    }
  }

  return result;
}

export async function queryPlannedWork(): Promise<PlannedWork> {
  try {
    const allEntries = await kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>("chain.listEntries", {
      fields: ["_id", "entryId", "name", "status", "workflowStatus", "collectionSlug", "stratum", "canonicalKey"],
    });
    if (!allEntries) return { uncommittedDrafts: [], inProgressEntries: [], openTensions: [] };
    return buildPlannedWork(allEntries);
  } catch {
    return { uncommittedDrafts: [], inProgressEntries: [], openTensions: [] };
  }
}

export function hasPlannedWork(work: PlannedWork): boolean {
  return (
    work.uncommittedDrafts.length > 0 ||
    work.inProgressEntries.length > 0 ||
    work.openTensions.length > 0
  );
}

/** ENT-yaic53: Format recovery block from Convex structured data. */
export function formatRecoveryBlock(block: {
  sessionId: string;
  status: 'timed_out' | 'completed';
  date: string;
  duration: string;
  created: { entryId: string; collection: string; name: string }[];
  modified: { entryId: string; collection: string; name: string }[];
  draftsNeedingAttention: { entryId: string; collection: string; name: string }[];
  relationsCreated: number;
  createdOverflow?: number;
  modifiedOverflow?: number;
  draftsOverflow?: number;
}): string[] {
  const fmt = (items: { entryId: string; collection: string; name: string }[], overflow?: number) =>
    items.map((e) => `${e.entryId} [${e.collection}] ${e.name}`).join(', ') +
    (overflow && overflow > 0 ? ` (+${overflow} more)` : '');

  const lines: string[] = [
    '## Previous Session Recovery',
    '',
    `Previous session — ${block.date}, ${block.duration}, status: ${block.status}`,
    `Created (${block.created.length + (block.createdOverflow ?? 0)}): ${fmt(block.created, block.createdOverflow)}`,
    `Modified (${block.modified.length + (block.modifiedOverflow ?? 0)}): ${fmt(block.modified, block.modifiedOverflow)}`,
    `Items pending review (${block.draftsNeedingAttention.length + (block.draftsOverflow ?? 0)}): ${fmt(block.draftsNeedingAttention, block.draftsOverflow)}`,
    `Relations created: ${block.relationsCreated}`,
    '',
  ];
  return lines;
}

export function buildPlannedWorkSection(
  work: PlannedWork,
  priorSessions: PriorSession[],
  recoveryBlock?: { sessionId: string; status: 'timed_out' | 'completed'; date: string; duration: string; created: { entryId: string; collection: string; name: string }[]; modified: { entryId: string; collection: string; name: string }[]; draftsNeedingAttention: { entryId: string; collection: string; name: string }[]; relationsCreated: number; createdOverflow?: number; modifiedOverflow?: number; draftsOverflow?: number } | null,
): string[] {
  if (!hasPlannedWork(work) && priorSessions.length === 0) return [];

  const lines: string[] = [];
  lines.push("## Continue from where you left off");
  lines.push("");

  if (work.inProgressEntries.length > 0) {
    const top = work.inProgressEntries[0];
    lines.push(`- **Active: ${top.name}** (${top.collection}) — want to continue?`);
    for (const entry of work.inProgressEntries.slice(1, 3)) {
      lines.push(`- ${entry.name} (${entry.collection})`);
    }
    if (work.inProgressEntries.length > 3) {
      lines.push(`- _...and ${work.inProgressEntries.length - 3} more in progress_`);
    }
  }

  if (work.uncommittedDrafts.length > 0) {
    const count = work.uncommittedDrafts.length;
    const label = count === 1 ? "1 item pending" : `${count} items pending`;
    const topNames = work.uncommittedDrafts
      .slice(0, 3)
      .map((d) => d.name)
      .join(", ");
    lines.push(`- **${label}** — ${topNames}${count > 3 ? ", ..." : ""}`);
  }

  if (work.openTensions.length > 0) {
    const top = work.openTensions[0];
    const count = work.openTensions.length;
    if (count === 1) {
      lines.push(`- **Open tension: ${top.name}** — discuss or capture a decision?`);
    } else {
      lines.push(`- **${count} open tensions** — top: ${top.name}`);
    }
  }

  if (priorSessions.length > 0 && !hasPlannedWork(work) && !recoveryBlock) {
    const last = priorSessions[0];
    // BUG FIX: `startedAt` is optional; `new Date(undefined)` throws RangeError on
    // .toISOString() ("Invalid time value") rather than producing a usable date.
    const date = last.startedAt !== undefined
      ? new Date(last.startedAt).toISOString().split("T")[0]
      : "unknown date";
    const created = Array.isArray(last.entriesCreated) ? last.entriesCreated.length : last.entriesCreated ?? 0;
    const modified = Array.isArray(last.entriesModified) ? last.entriesModified.length : last.entriesModified ?? 0;
    if (created > 0 || modified > 0) {
      lines.push(`- **Last session** (${date}): ${created} created, ${modified} modified`);
    }
  }

  if (recoveryBlock) {
    lines.push('');
    lines.push(...formatRecoveryBlock(recoveryBlock));
  }

  lines.push("");
  return lines;
}
